import type { AccessScopeType, AccessTenant, AccessibleLocation, Organization, OrganizationUnit } from '../types/access'

const shortId = (id?: string | null) => (id ? `${id.slice(0, 8)}...` : '')

export interface ScopeLookupContext {
  tenant?: AccessTenant | null
  tenantId?: string | null
  organizations?: Organization[]
  organizationUnits?: OrganizationUnit[]
  locations?: AccessibleLocation[]
}

export const formatTenantLabel = (tenant?: AccessTenant | null, tenantId?: string | null) => {
  if (tenant?.name && tenant.slug) return `${tenant.name} (${tenant.slug})`
  if (tenant?.name) return tenant.name
  if (tenant?.slug) return tenant.slug
  return `Unknown tenant (${shortId(tenant?.id || tenantId) || 'not loaded'})`
}

export const formatOrganizationLabel = (org?: Organization | null) => {
  if (!org) return 'Unknown organization'
  const type = org.org_type ? org.org_type.replace(/_/g, ' ') : ''
  const slug = org.slug ? ` (${org.slug})` : ''
  return [org.name || `Unknown organization (${shortId(org.id)})`, type].filter(Boolean).join(' - ') + slug
}

export const formatScopeLabel = (
  scope: { scope_type?: AccessScopeType | string | null; scopeType?: AccessScopeType | string | null; scope_id?: string | null; scopeId?: string | null },
  lookupContext: ScopeLookupContext,
) => {
  const scopeType = scope.scope_type || scope.scopeType || 'organization'
  const scopeId = scope.scope_id ?? scope.scopeId ?? null
  if (scopeType === 'tenant') return `Tenant: ${formatTenantLabel(lookupContext.tenant, lookupContext.tenantId)}`
  if (scopeType === 'organization') {
    const org = lookupContext.organizations?.find((item) => item.id === scopeId) || lookupContext.organizations?.[0]
    return `Organization: ${org?.name || `Unknown organization (${shortId(scopeId)})`}`
  }
  if (scopeType === 'organization_unit') {
    const unit = lookupContext.organizationUnits?.find((item) => item.id === scopeId)
    return `OU: ${unit?.path || unit?.name || `Unknown OU (${shortId(scopeId)})`}`
  }
  if (scopeType === 'location') {
    const location = lookupContext.locations?.find((item) => item.id === scopeId)
    return `Location: ${location?.name || location?.label || location?.location_key || `Unknown location (${shortId(scopeId)})`}`
  }
  return `${String(scopeType).replace(/_/g, ' ')}: ${scopeId ? shortId(scopeId) : 'current scope'}`
}
